'use client';

import classNames from 'classnames';
import { type ComponentProps, type FC, type ReactEventHandler, type ReactNode, useId } from 'react';
import { FieldFooter } from '../../atoms/field-footer';
import { ExclamationMarkTriangleXs } from '../../icons/symbols/exclamation-mark-triangle-xs';
import { LabelSuffix } from '../helpers/label-suffix';
import styles from './textarea.module.scss';

export type TextareaProps = ComponentProps<'textarea'> & {
  centered?: boolean;
  className?: string;
  'data-testid'?: string;
  errorMessage?: string;
  footer?: ReactNode;
  forcedError?: string;
  inputClassName?: string;
  label: string;
  hideLabelSuffix?: boolean;
};

const defaultOnInvalid: ReactEventHandler<HTMLTextAreaElement> = (e) => e.preventDefault();

export const Textarea: FC<TextareaProps> = ({
  centered,
  className,
  'data-testid': testId,
  errorMessage,
  footer,
  forcedError,
  inputClassName,
  label,
  onInvalid = defaultOnInvalid,
  placeholder = ' ',
  ...props
}) => {
  const id = useId();
  const labelId = `${id}-label`;
  const footerId = footer ? `${id}-footer` : undefined;

  return (
    <div className={classNames(styles.wrapper, className)} data-testid={testId}>
      <label className={classNames(className, styles['field-wrapper'])}>
        <div className={classNames(styles.label, centered && styles['label-centered'])} id={labelId}>
          {label}
          {!props.hideLabelSuffix && <LabelSuffix required={props.required} disabled={props.disabled} />}
        </div>
        <textarea
          {...props}
          aria-labelledby={[labelId, footerId].join(' ')}
          className={classNames(inputClassName, styles.input, centered && styles['input-centered'])}
          onInvalid={onInvalid}
          placeholder={placeholder}
        />
      </label>
      {forcedError ? (
        <FieldFooter
          className={classNames(styles['error-message'], styles['forced-error'])}
          data-testid="field-error"
          error
          icon={<ExclamationMarkTriangleXs />}
        >
          {forcedError}
        </FieldFooter>
      ) : (
        <>
          {footer && (
            <FieldFooter className={classNames(styles.footer)} data-testid="field-footer" id={footerId}>
              {footer}
            </FieldFooter>
          )}
          {errorMessage && (
            <FieldFooter
              className={styles['error-message']}
              data-testid="field-error"
              error
              icon={<ExclamationMarkTriangleXs />}
            >
              {errorMessage}
            </FieldFooter>
          )}
        </>
      )}
    </div>
  );
};
